import type { Activity, Analysis } from '@trainme/db/client';
import Loading from '@/app/components/Loading';
import { HiSparkles } from 'react-icons/hi';
import { TimePeriod } from '@/utils/prismaEnums';
import { endOfDay, endOfWeek, startOfDay } from 'date-fns';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { useTimelyAnalysisStore } from '@/app/stores/timelyAnalysisStore';

export type AnalyzeByActivitiesResult = {
  matchCount?: number;
  analyses?: Analysis[];
};

// shows as the coach icon
export const AnalyzeButton = ({
  activities,
  onSuccess,
}: {
  activities: Activity[];
  onSuccess?: (response: AnalyzeByActivitiesResult) => void | Promise<unknown>;
}) => {
  const utils = trpc.useUtils();

  const { mutate: analyze, isPending } =
    trpc.analysis.analyzeByActivities.useMutation({
      onSuccess: async (response) => {
        const analysisCount = response?.matchCount || 0;
        toast.success(
          `Analysis complete, ${analysisCount} ${
            analysisCount === 1 ? 'activity' : 'activities'
          } matched`
        );

        // Invalidate queries to refresh the UI
        await utils.analysis.getMany.invalidate();
        for (const activity of activities) {
          await utils.analysis.getActivityAnalysis.invalidate({
            activityId: activity.id,
          });
        }

        // Call onSuccess if provided
        if (onSuccess) {
          await onSuccess(response);
        }
      },
      onError: () => {
        toast.error('Failed to analyze activities.');
      },
    });

  return (
    <button
      className='text-orange-400 flex items-center gap-2'
      onClick={() => analyze({ activityIds: activities.map((a) => a.id) })}
      disabled={isPending}
    >
      {!isPending && <HiSparkles size={14} />}
      <Loading loading={isPending} size={14} />
    </button>
  );
};

// Button to analyze a day using the new time-based analysis
export const AnalyzeDayButton = ({
  date,
  activitiesCount = 0,
  workoutsCount = 0,
  onSuccess,
}: {
  date: Date;
  activitiesCount?: number;
  workoutsCount?: number;
  onSuccess?: () => void | Promise<unknown>;
}) => {
  const utils = trpc.useUtils();

  const mergeTimelyAnalyses = useTimelyAnalysisStore(
    (state) => state.mergeTimelyAnalyses
  );
  const normalizedDate = startOfDay(date);

  const { mutate: analyze, isPending } = trpc.analysis.analyzeDaily.useMutation(
    {
      onSuccess: async (analysis) => {
        if (!analysis) {
          toast.info('No activity found for this day yet.');
          return;
        }

        toast.success('Daily analysis complete!');

        // Invalidate the query cache
        await utils.analysis.getTimelyAnalysis.invalidate({
          period: TimePeriod.DAILY,
          startDate: normalizedDate,
        });

        // Merge the new analysis into the store
        mergeTimelyAnalyses([analysis]);

        if (onSuccess) {
          await onSuccess();
        }
      },
      onError: () => {
        toast.error('Failed to analyze day.');
      },
    }
  );

  // Hide the button only for future days
  const today = startOfDay(new Date());
  if (normalizedDate > today) {
    return null;
  }

  const hasData = activitiesCount > 0 || workoutsCount > 0;
  const buttonDisabled = isPending || !hasData;

  return (
    <button
      className={`flex items-center gap-2 transition-opacity ${
        buttonDisabled
          ? 'text-slate-400 cursor-not-allowed opacity-50'
          : 'text-orange-400'
      }`}
      onClick={() => {
        if (buttonDisabled) {
          return;
        }
        analyze({ date });
      }}
      disabled={buttonDisabled}
    >
      {!isPending && <HiSparkles size={14} />}
      <Loading loading={isPending} size={14} />
    </button>
  );
};

// Button to analyze a week using the new time-based analysis
export const AnalyzeWeekButton = ({
  weekStart,
  activitiesCount = 0,
  workoutsCount = 0,
  onSuccess,
}: {
  weekStart: Date; // Monday of the week
  activitiesCount?: number;
  workoutsCount?: number;
  onSuccess?: () => void | Promise<unknown>;
}) => {
  const utils = trpc.useUtils();
  const mergeTimelyAnalyses = useTimelyAnalysisStore(
    (state) => state.mergeTimelyAnalyses
  );

  const { mutate: analyze, isPending } =
    trpc.analysis.analyzeWeekly.useMutation({
      onSuccess: async () => {
        toast.success('Weekly analysis complete!');
        // Normalize date to start of day to match server-side normalization
        const normalizedDate = startOfDay(weekStart);

        // Invalidate the query cache
        await utils.analysis.getTimelyAnalysis.invalidate({
          period: TimePeriod.WEEKLY,
          startDate: normalizedDate,
        });

        // Call the onSuccess callback to trigger refetch in the component
        const analysis = await utils.analysis.getTimelyAnalysis.fetch({
          period: TimePeriod.WEEKLY,
          startDate: normalizedDate,
        });
        if (analysis) {
          mergeTimelyAnalyses([analysis]);
        }

        if (onSuccess) {
          await onSuccess();
        }
      },
      onError: () => {
        toast.error('Failed to analyze week.');
      },
    });

  // Only show if the week has ended
  const today = startOfDay(new Date());
  const weekEnd = endOfDay(endOfWeek(weekStart, { weekStartsOn: 1 }));

  if (weekEnd >= today) {
    return null;
  }

  // Only show if there is data to analyze
  if (activitiesCount === 0 && workoutsCount === 0) {
    return null;
  }

  return (
    <button
      className='text-orange-400 flex items-center gap-2'
      onClick={() => analyze({ startDate: weekStart })}
      disabled={isPending}
    >
      {!isPending && <HiSparkles size={14} />}
      <Loading loading={isPending} size={14} />
    </button>
  );
};
